// « Photo Vivante » — adding the video, whoever does it: the team on the
// order page (LivingPhotoCard) or the client in the album editor
// (LivingEditorPanel). Same steps, same files (worker routes/living.ts):
//   1. the sharpest frame becomes the printed photo (livingStill.bestStill),
//      and we keep the second it was taken at — the camera page starts the
//      video there, so the print looks like it starts moving;
//   2. the ORIGINAL video goes up as it is (since 2026-11-04): our server
//      makes the played file — 720p, smooth, up to 2 min, plays while it
//      downloads (deploy/living-prep, migration 290). Only a file above the
//      storage limit is made lighter here first;
//   3. the link works as soon as the server is done (1–3 min).
import { bestStill } from './livingStill';
import type { LivingUploader } from './api';

export const LIVING_MAX_BYTES = 200 * 1024 * 1024;   // bucket `living` limit (migration 285)
export const LIVING_MAX_SECONDS = 60;
export const LIVING_LONG_SIDE = 1280;

export type LivingStep = { label: string; pct: number };

/** File extension the worker accepts (mp4 / mov / webm). Pure. */
export function livingExt(name: string, type: string): 'mp4' | 'mov' | 'webm' {
  const fromName = (name.split('.').pop() || '').toLowerCase();
  if (fromName === 'mp4' || fromName === 'mov' || fromName === 'webm') return fromName;
  if (type === 'video/quicktime') return 'mov';
  if (type === 'video/webm') return 'webm';
  return 'mp4';
}

/** Output size keeping the shape, long side at most `max`, even numbers. Pure. */
export function lighterSize(w: number, h: number, max = LIVING_LONG_SIDE): { w: number; h: number } {
  const k = Math.min(1, max / Math.max(w, h));
  const even = (n: number) => Math.max(2, Math.round((n * k) / 2) * 2);
  return { w: even(w), h: even(h) };
}

export function put(url: string, body: Blob, type: string, onProgress?: (p: number) => void): Promise<void> {
  return new Promise((ok, ko) => {
    const x = new XMLHttpRequest();
    x.open('PUT', url);
    x.setRequestHeader('Content-Type', type);
    x.setRequestHeader('x-upsert', 'true');
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
    x.onload = () => (x.status >= 200 && x.status < 300 ? ok() : ko(new Error(`Envoi refusé (HTTP ${x.status}).`)));
    x.onerror = () => ko(new Error('Envoi interrompu. Vérifiez la connexion, puis réessayez.'));
    x.send(body);
  });
}

function mp4Type(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return ['video/mp4;codecs=avc1,mp4a.40.2', 'video/mp4;codecs=avc1', 'video/mp4'].find((t) => MediaRecorder.isTypeSupported?.(t)) ?? null;
}

/** Below this, a remade video looks jerky on the page. */
export const MIN_RECORD_FPS = 20;
export class RecordTooSlow extends Error {
  fps: number;
  constructor(fps: number) {
    super(`La vidéo a été refaite trop lentement (${Math.round(fps)} images/s) : l’onglet est passé en arrière-plan ou l’appareil est trop lent. Gardez cette page ouverte et visible, sur ordinateur, puis réessayez.`);
    this.fps = fps;
  }
}

/**
 * Plays the video once into a `size` canvas, drawing each frame with `draw`,
 * and records it (mp4, sound kept). Takes as long as the video (max 60 s).
 * Returns null when the browser cannot record mp4.
 */
export async function recordVideo(
  file: File, size: { w: number; h: number },
  draw: (g: CanvasRenderingContext2D, video: HTMLVideoElement) => void,
  onProgress?: (p: number) => void,
): Promise<Blob | null> {
  const type = mp4Type();
  const canvasOk = typeof HTMLCanvasElement !== 'undefined' && 'captureStream' in HTMLCanvasElement.prototype;
  if (!type || !canvasOk) return null;
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.src = url; video.playsInline = true; video.preload = 'auto';
  // On the page, invisible: Safari (and some Chrome builds) barely decode a
  // video that is not in the document — 5 images/s on the owner's Mac
  // (2026-11-04). A 2 px box at the corner, never seen, never clicked.
  video.setAttribute('aria-hidden', 'true');
  Object.assign(video.style, { position: 'fixed', left: '0', bottom: '0', width: '2px', height: '2px', opacity: '0.01', pointerEvents: 'none', zIndex: '-1' });
  document.body.appendChild(video);
  let ctx: AudioContext | null = null;
  try {
    await new Promise<void>((ok, ko) => { video.onloadedmetadata = () => ok(); video.onerror = () => ko(new Error('video')); });
    const { w, h } = size;
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    const g = canvas.getContext('2d')!;
    // One recorded frame per frame OF THE VIDEO when the browser can say when
    // a frame is shown (requestVideoFrameCallback): a 24 fps clip stays 24 fps.
    // A fixed 30 fps capture repeats every 4th frame — a judder you see on the
    // page (Japan demo, 2026-11-04). Older browsers keep the 30 fps capture.
    type FrameCb = (cb: () => void) => number;
    const onFrame = (video as unknown as { requestVideoFrameCallback?: FrameCb }).requestVideoFrameCallback?.bind(video);
    const stream = onFrame ? canvas.captureStream(0) : canvas.captureStream(30);
    const track = stream.getVideoTracks()[0] as MediaStreamTrack & { requestFrame?: () => void };
    const perFrame = !!onFrame && typeof track?.requestFrame === 'function';
    try {   // the sound goes through WebAudio (Safari has no video.captureStream)
      ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const dest = ctx.createMediaStreamDestination();
      ctx.createMediaElementSource(video).connect(dest);   // not to the speakers: silent while it works
      await ctx.resume().catch(() => {});
      dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
    } catch { video.muted = true; }
    const rec = new MediaRecorder(stream, { mimeType: type, videoBitsPerSecond: 4_000_000 });
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    const stopped = new Promise<void>((ok) => { rec.onstop = () => ok(); });
    const end = Math.min(video.duration || LIVING_MAX_SECONDS, LIVING_MAX_SECONDS);
    let raf = 0, stop = false, frames = 0;
    const step = () => { draw(g, video); frames++; if (perFrame) track.requestFrame!(); onProgress?.(Math.min(1, video.currentTime / end)); if (video.currentTime >= end) video.pause(); };
    const tick = () => { if (stop) return; step(); if (!video.paused) raf = requestAnimationFrame(tick); };
    const tickFrame = () => { if (stop) return; step(); if (!video.paused) onFrame!(tickFrame); };
    draw(g, video);
    if (perFrame) track.requestFrame!();
    rec.start(500);
    await video.play();
    if (perFrame) onFrame!(tickFrame); else raf = requestAnimationFrame(tick);
    await new Promise<void>((ok) => { video.onpause = () => ok(); video.onended = () => ok(); });
    stop = true;
    cancelAnimationFrame(raf);
    rec.stop();
    await stopped;
    const out = new Blob(chunks, { type: 'video/mp4' });
    if (!out.size) return null;
    // A tab sent to the background (or a slow phone) draws a few frames a
    // second: the remake then plays like a slideshow on the page (Japan demo,
    // 19:35, 8.7 fps). Said out loud instead of uploaded.
    const fps = frames / Math.max(0.5, video.currentTime || end);
    // Per-frame recording: compare with the frames the clip really showed
    // (a 15 fps clip is fine at 15). Otherwise: the plain floor.
    const shown = perFrame ? video.getVideoPlaybackQuality?.().totalVideoFrames ?? 0 : 0;
    if (shown > 0 ? frames < shown * 0.8 : fps < MIN_RECORD_FPS) throw new RecordTooSlow(fps);
    return out;
  } catch (e) {
    if (e instanceof RecordTooSlow) throw e;
    return null;
  } finally {
    video.remove();
    URL.revokeObjectURL(url);
    ctx?.close().catch(() => {});
  }
}

/**
 * A lighter copy of a big phone video (720p-class mp4, sound kept).
 * Returns null when the browser cannot (the original is then sent as is).
 */
export async function lighterVideo(file: File, onProgress?: (p: number) => void): Promise<Blob | null> {
  const dims = await new Promise<{ w: number; h: number } | null>((ok) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video'); v.preload = 'metadata'; v.src = url;
    v.onloadedmetadata = () => { URL.revokeObjectURL(url); ok({ w: v.videoWidth, h: v.videoHeight }); };
    v.onerror = () => { URL.revokeObjectURL(url); ok(null); };
  });
  if (!dims) return null;
  const size = lighterSize(dims.w, dims.h);
  const out = await recordVideo(file, size, (g, v) => g.drawImage(v, 0, 0, size.w, size.h), onProgress).catch((e) => {
    if (e instanceof RecordTooSlow) return null;   // the original is sent as is
    throw e;
  });
  return out && out.size < file.size ? out : null;
}

/** The original, or a lighter copy when it is above the storage limit. */
export async function originalOrLighter(file: File, onStep: (s: LivingStep) => void, from: number, to: number): Promise<{ video: Blob; ext: 'mp4' | 'mov' | 'webm' }> {
  if (file.size <= LIVING_MAX_BYTES) return { video: file, ext: livingExt(file.name, file.type) };
  onStep({ label: 'Vidéo très lourde : préparation…', pct: from });
  const light = await lighterVideo(file, (p) => onStep({ label: 'Vidéo très lourde : préparation…', pct: from + p * (to - from) }));
  if (!light || light.size > LIVING_MAX_BYTES) throw new Error('Vidéo trop lourde (plus de 200 Mo). Envoyez-la plus courte, ou depuis WhatsApp (elle y est déjà allégée).');
  return { video: light, ext: 'mp4' };
}

/** The whole flow. Returns the album's link. Throws a French message. */
export async function addLivingVideo(file: File, albumIndex: number, api: LivingUploader, onStep: (s: LivingStep) => void): Promise<string> {
  if (!file.type.startsWith('video/') && !/\.(mp4|mov|webm)$/i.test(file.name)) throw new Error('Choisissez une vidéo.');
  onStep({ label: 'Choix de la plus belle image de la vidéo…', pct: 3 });
  const still = await bestStill(file, (p) => onStep({ label: 'Choix de la plus belle image de la vidéo…', pct: 3 + p * 27 }));
  const { video, ext } = await originalOrLighter(file, onStep, 30, 60);

  onStep({ label: 'Envoi…', pct: 62 });
  const up = await api.upload(albumIndex, ext);
  await put(up.upload.still, still.blob, 'image/jpeg');
  onStep({ label: 'Envoi de la vidéo…', pct: 66 });
  const videoType = video === file ? (file.type || `video/${ext === 'mov' ? 'quicktime' : ext}`) : 'video/mp4';
  await put(up.upload.video, video, videoType, (p) => onStep({ label: 'Envoi de la vidéo…', pct: 66 + p * 30 }));
  onStep({ label: 'Presque fini…', pct: 97 });
  await api.ready(albumIndex, up.paths, still.at);
  return up.link;
}
